/**
 * Phone Display Component
 *
 * Displays a phone number with country flag.
 * Uses the same country code parsing as PhoneInput for consistency.
 */

import { Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { parsePhoneNumber, getCountryByCode } from '@/lib/validation';

export interface PhoneDisplayProps {
  /** The full phone number (e.g., "+49 176 12345678") */
  value: string;
  /** Whether to show the phone icon */
  showIcon?: boolean;
  /** Whether to show the country flag (default: false) */
  showFlag?: boolean;
  /** Icon size class */
  iconClassName?: string;
  /** Additional class name */
  className?: string;
  /** Whether to make it a clickable tel: link */
  asLink?: boolean;
}

export function PhoneDisplay({
  value,
  showIcon = false,
  showFlag = false,
  iconClassName = 'w-4 h-4',
  className,
  asLink = false,
}: PhoneDisplayProps) {
  if (!value) return null;

  // Parse the phone number to get country code
  const { countryCode, localNumber } = parsePhoneNumber(value);
  const country = getCountryByCode(countryCode);

  // Format display: optionally flag + dial code + local number
  const displayValue = country
    ? showFlag
      ? `${country.flag} ${country.dialCode} ${localNumber}`
      : `${country.dialCode} ${localNumber}`
    : value;

  const content = (
    <>
      {showIcon && <Phone className={cn('text-slate-400', iconClassName)} />}
      <span>{displayValue}</span>
    </>
  );

  if (asLink) {
    return (
      <a
        href={`tel:${value}`}
        className={cn(
          'flex items-center gap-1.5 hover:text-orange-400 transition-colors',
          className
        )}
      >
        {content}
      </a>
    );
  }

  return (
    <span className={cn('flex items-center gap-1.5', className)}>
      {content}
    </span>
  );
}
